import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Drawer,
  useTheme as useMUITheme,
  useMediaQuery,
  Alert,
  CircularProgress,
  LinearProgress,
  IconButton,
} from '@mui/material';
import { useTheme } from '../../theme/ThemeProvider';
import { CompanyImagesSidebar } from '../PDFBuilder/PDFeditor/PDFeditorComponets/CompanyImagesSidebar';
import {
  Image as ImageIcon,
  Description,
  Download,
  ExpandMore,
  ChevronRight,
  ArrowBack,
  CheckCircle,
  Sync,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import { preprocessImageUrl } from '../../utils/imageUtils';
import { RootState, PropertyEntryModel } from '../../types/models';
import { serializePropertyEntry, serializeReportData } from '../../utils/firebaseSerializer';
import {
  setLoading,
  setExporting,
  setExportStatus,
  setError,
  clearError,
  createDocument,
  setCurrentPage,
  setReportData,
  setAvailableAssets,
} from '../../store/slices/pdfEditorSlice';
import DocumentEditor, { SimpleDocumentEditorHandle, OverlayPositionData } from '../PDFBuilder/PDFeditor/SimpleDocumentEditor';
import ExportErrorBoundary from '../PDFBuilder/PDFeditor/ExportErrorBoundary';
import NewFileTemplateDialog from '../PDFBuilder/PDFeditor/NewFileTemplateDialog';
import { MetadataOverlay } from './MetadataOverlay';
import { FirebaseService } from '../../services/FirebaseService';

interface PDFEditorProps {
  reportId?: string;
  onBack?: () => void;
  readOnly?: boolean;
}

const PDFEditor: React.FC<PDFEditorProps> = ({ reportId: propReportId, onBack }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const muiTheme = useMUITheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const { reportId: urlReportId } = useParams<{ reportId: string }>();
  const reportId = propReportId || urlReportId;
  const { user } = useAuth();

  const {
    currentDocument,
    currentPageIndex,
    isLoading,
    isExporting,
    exportStatus,
    error,
    reportData,
    availableAssets,
  } = useSelector((state: RootState) => state.pdfEditor);

  const [sidebarTab, setSidebarTab] = useState(0);
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved');
  const [exportProgress, setExportProgress] = useState(0);
  const [newFileDialogOpen, setNewFileDialogOpen] = useState(false);
  
  // Ref for document editor to access export functionality
  const documentEditorRef = React.useRef<SimpleDocumentEditorHandle>(null);

  // Step 3: React Portal Overlay System
  const [overlayPositions, setOverlayPositions] = useState<OverlayPositionData[]>([]);

  // Handle overlay position updates from SimpleDocumentEditor
  const handleOverlayPositionsChange = useCallback((positions: OverlayPositionData[]) => {
    setOverlayPositions(positions);
    
    // Log during export for debugging
    if (isExporting) {
      //console.log(`📍 Final overlay positions updated for export: ${positions.length} overlays`);
    }
  }, [isExporting]);

  // No scroll listeners needed - overlays scroll naturally with position: absolute

  // Handle responsive sidebar behavior - close on mobile, open on desktop
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  // Canvas dimensions - A4 size for 1:1 PDF scaling
  const canvasWidth = 595;  // A4 width in points (8.27" at 72 DPI)
  const canvasHeight = 842; // A4 height in points (11.69" at 72 DPI)

  // Check if this is a shared report from URL params
  const searchParams = new URLSearchParams(window.location.search);
  const isSharedReport = searchParams.get('shared') === 'true';

  useEffect(() => {
    if (reportId && user) {
      // Add a small delay to ensure authentication is fully established
      const timeoutId = setTimeout(() => {
        loadReportData(reportId, isSharedReport);
      }, 100);
      
      return () => clearTimeout(timeoutId);
    } else if (!reportId) {
      // Create new document
      dispatch(createDocument({ title: 'New PDF Report' }));
    }
  }, [reportId, user, isSharedReport, dispatch]);

  // Ensure we always have a document to work with
  useEffect(() => {
    if (!currentDocument && !isLoading && reportId && user) {
      // If we don't have a document after trying to load, create a fallback
      dispatch(createDocument({ 
        title: `Report ${reportId} - PDF Editor`,
        reportId: reportId 
      }));
    } else if (!currentDocument && !isLoading && !reportId) {
      // Create a document even without reportId
      dispatch(createDocument({ 
        title: 'New PDF Report'
      }));
    }
  }, [currentDocument, isLoading, reportId, user, dispatch]);

  // Poll export progress during export
  useEffect(() => {
    let progressInterval: NodeJS.Timeout | null = null;
    
    if (isExporting && documentEditorRef.current) {
      //console.log('🔄 Starting progress polling...');
      setExportProgress(0); // Reset to 0 when starting
      
      progressInterval = setInterval(() => {
        if (documentEditorRef.current) {
          const currentProgress = documentEditorRef.current.getExportProgress();
          const isCurrentlyExporting = documentEditorRef.current.isExporting();
          
          //console.log(`📊 Progress update: ${currentProgress}%, isExporting: ${isCurrentlyExporting}`);
          setExportProgress(currentProgress);
        }
      }, 100); // Poll every 100ms for smooth updates
    } else if (!isExporting) {
      // console.log('🏁 Export finished, resetting progress');
      // Keep the progress bar visible briefly to show completion
      setTimeout(() => setExportProgress(0), 1000);
    }
    
    return () => {
      if (progressInterval) {
        // console.log('🛑 Clearing progress interval');
        clearInterval(progressInterval);
      }
    };
  }, [isExporting]);

  const loadReportData = async (id: string, isShared: boolean = false) => {
    if (!user) {
      dispatch(setError('User not authenticated'));
      return;
    }

    // console.log('🔥 PDFEditor - Loading report data for:', { reportId: id, userId: user.uid, isShared });
    dispatch(setLoading(true));
    try {
      let report;
      let entries;

      if (isShared) {
        // Handle shared reports
        // console.log('📤 Loading shared report data...');
        const sharedReports = await FirebaseService.getSharedReports();
        report = sharedReports.find(r => r.id === id);
        entries = await FirebaseService.fetchSharedEntries(id);
        // console.log('✅ Loaded shared report:', report?.title, 'with', entries.length, 'entries');
      } else {
        // Handle personal reports
        // console.log('👤 Loading personal report data...');
        report = await FirebaseService.getReport(id, user.uid);
        entries = await FirebaseService.getEntriesForReport(id, user.uid);
        // console.log('✅ Loaded personal report:', report?.title, 'with', entries.length, 'entries');
      }

      if (report) {
        // Serialize report data to remove Date objects before storing in Redux
        dispatch(setReportData(serializeReportData(report)));
        
        // Process entries and create fully serialized assets  
        const processedAssets = {
          images: entries.flatMap((entry: PropertyEntryModel) => {
            return entry.images || [];
          }),
          entries: entries.map(entry => serializePropertyEntry(entry)) as any, // Cast to avoid type mismatch
        };
        
        dispatch(setAvailableAssets(processedAssets));
        dispatch(createDocument({ 
          title: `${report.title} - PDF Report${isShared ? ' (Shared)' : ''}`,
          reportId: id 
        }));
      } else {
        // Create a fallback document with sample assets
        const sampleAssets = {
          images: [],
          entries: []
        };
        
        dispatch(setAvailableAssets(sampleAssets));
        dispatch(createDocument({ 
          title: `Report ${id} - PDF Editor ${isShared ? '(Shared - Offline Mode)' : '(Offline Mode)'}`,
          reportId: id 
        }));
      }
    } catch (error) {
      dispatch(setError(`Failed to load ${isShared ? 'shared' : ''} report data`));
      console.error('Error loading report:', error);
      // Still create a document so user can start editing
      dispatch(createDocument({ 
        title: `Report ${id} - PDF Editor${isShared ? ' (Shared)' : ''}`,
        reportId: id 
      }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handlePageThumbnailClick = (pageIndex: number) => {
    dispatch(setCurrentPage(pageIndex));
  };

  const toggleEntryExpansion = (entryId: string) => {
    setExpandedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  // Helper function to load images as base64 with CORS handling
  const loadImageAsBase64 = (url: string): Promise<string | null> => {
    return new Promise((resolve) => {
      try {
        // For Firebase Storage URLs, we can use the fetch API with no-cors mode
        // or convert to a data URL using canvas
        const img = new Image();
        
        // Set crossOrigin to handle CORS
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          try {
            // Create canvas to convert image to base64
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
              console.error('Failed to get canvas context');
              resolve(null);
              return;
            }
            
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            
            // Draw image to canvas
            ctx.drawImage(img, 0, 0);
            
            // Convert to base64
            const dataURL = canvas.toDataURL('image/jpeg', 0.8);
            resolve(dataURL);
          } catch (canvasError) {
            console.error('Canvas conversion error:', canvasError);
            resolve(null);
          }
        };
        
        img.onerror = (error) => {
          console.error('Image load error:', error);
          
          // Fallback: try to fetch with different approach
          fetchImageAsBase64(url).then(resolve).catch(() => resolve(null));
        };
        
        // Try to load the image
        img.src = url;
        
      } catch (error) {
        console.error('loadImageAsBase64 error:', error);
        resolve(null);
      }
    });
  };

  // Alternative fetch-based approach for images
  const fetchImageAsBase64 = async (url: string): Promise<string | null> => {
    try {
      // For Firebase Storage, we can try fetching the image data directly
      const response = await fetch(url, {
        mode: 'cors',
        credentials: 'omit'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('fetchImageAsBase64 error:', error);
      return null;
    }
  };

  // Handle client-side PDF export (fast and reliable)
  const handleExport = async () => {
    //console.log('🚀 Starting client-side PDF export...');

    if (!documentEditorRef.current) {
      console.error('❌ Document editor not ready');
      alert('Document not ready. Please try again.');
      return;
    }

    try {
      dispatch(setExporting(true));
      dispatch(setExportStatus('Generating PDF...'));
      
      // Use the client-side export from SimpleDocumentEditor
      await documentEditorRef.current.handleClientSideExport();
      
    } catch (error) {
      console.error('❌ Client-side PDF export failed:', error);
      
      let errorMessage = 'Failed to export PDF. Please try again.';
      
      if (error instanceof Error) {
        errorMessage = `Export failed: ${error.message}`;
      }
      
      alert(errorMessage);
      dispatch(setError(errorMessage));
    } finally {
      dispatch(setExporting(false));
      dispatch(setExportStatus(null));
      setExportProgress(0); // Reset progress when export completes
    }
  };

  // Handle file menu actions
  const handleFileMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    // Open template dialog directly instead of showing menu
    setNewFileDialogOpen(true);
  };

  const handleCreateBlankDocument = () => {
    //console.log('🔄 Creating new blank document');
    
    // Reset the document content to completely blank
    if (documentEditorRef.current) {
      documentEditorRef.current.resetToBlankDocument();
    }
    
    // Update the document title in Redux
    dispatch(createDocument({ 
      title: 'New PDF Report'
    }));
    
    // Reset save status
    setSaveStatus('saved');
    
    // Clear any loading states and errors
    dispatch(setLoading(false));
    dispatch(clearError());
  };

  const handleCreateFromTemplate = (selectedEntries: any[]) => {
    //console.log('🎨 Creating document from template with', selectedEntries.length, 'entries');
    
    // Create document from template
    if (documentEditorRef.current) {
      documentEditorRef.current.createFromTemplate(selectedEntries);
    }
    
    // Update the document title in Redux
    dispatch(createDocument({ 
      title: `Template Report (${selectedEntries.length} entries)`
    }));
    
    // Reset save status to unsaved since we have content
    setSaveStatus('unsaved');
    
    // Clear any loading states and errors
    dispatch(setLoading(false));
    dispatch(clearError());
  };

  // Sidebar content
  const renderAssetPanel = () => (
    <Box>
      {/* Expandable Report Entries */}
      <Typography 
        variant="subtitle2" 
        sx={{ 
          mt: 2, 
          mb: 1,
          color: theme.colors.green,
          fontWeight: 500,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
        }}
      >
        Entries ({availableAssets.entries.length})
      </Typography>
      
      {availableAssets.entries.map((entry) => {
        const isExpanded = expandedEntries.has(entry.id);
        const entryImages = availableAssets.images.filter(img => 
          entry.images?.some(entryImg => entryImg.id === img.id)
        );
        
        return (
          <Box key={entry.id} sx={{ mb: 2 }}>
            {/* Entry Header - Clickable to expand/collapse */}
            <Paper
              elevation={0}
              sx={{
                p: 1,
                cursor: 'pointer',
                border: '2px solid #2E7D4D',
                borderRadius: '8px',
                bgcolor: '#FFFFFF',
                '&:hover': { 
                  backgroundColor: '#F9F5EF',
                  borderColor: theme.colors.green
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
              onClick={() => toggleEntryExpansion(entry.id)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Description sx={{ color: theme.colors.green }} />
                <Box>
                  <Typography variant="body2" fontWeight="medium" sx={{ color: theme.colors.green }}>
                    {entry.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(46, 125, 77, 0.7)' }}>
                    {entryImages.length} images
                  </Typography>
                </Box>
              </Box>
              {isExpanded ? <ExpandMore sx={{ color: theme.colors.green }} /> : <ChevronRight sx={{ color: theme.colors.green }} />}
            </Paper>

            {/* Expanded Content */}
            {isExpanded && (
              <Box sx={{ mt: 1, pl: 2, pr: 1 }}>
                {/* Entry Notes */}
                {entry.note && (
                  <Paper sx={{ p: 1, mb: 1, backgroundColor: '#f9f9f9' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Notes:
                    </Typography>
                    <Typography variant="body2">{entry.note}</Typography>
                  </Paper>
                )}

                {/* Entry Images */}
                {entryImages.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Images ({entryImages.length}):
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1}>
                      {entryImages.map((image) => (
                        <Paper
                          key={image.id}
                          sx={{
                            p: 1,
                            cursor: 'grab',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            '&:hover': { elevation: 3 },
                          }}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('application/json', JSON.stringify({
                              type: 'image',
                              data: {
                                ...image,
                                photoURL: preprocessImageUrl(image.photoURL)
                              },
                            }));
                          }}
                        >
                          <Box
                            sx={{
                              width: 50,
                              height: 35,
                              borderRadius: 1,
                              backgroundColor: '#f5f5f5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                            }}
                          >
                            <img
                              src={preprocessImageUrl(image.photoURL)}
                              alt="Property"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                              onError={(e) => {
                                console.error('🔥 Image failed to load:', image.photoURL);
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                target.parentElement!.innerHTML = `
                                  <div style="
                                    width: 100%; 
                                    height: 100%; 
                                    display: flex; 
                                    align-items: center; 
                                    justify-content: center; 
                                    background: #e0e0e0; 
                                    color: #666; 
                                    font-size: 8px;
                                    text-align: center;
                                  ">
                                    Error
                                  </div>
                                `;
                              }}
                              onLoad={() => {
                                // console.log('✅ Image loaded successfully:', preprocessImageUrl(image.photoURL));
                              }}
                            />
                          </Box>
                          <Typography variant="caption">
                            Image
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Draggable Entry (for adding entire entry to canvas) */}
                <Box sx={{ mt: 1 }}>
                  <Paper
                    sx={{
                      p: 1,
                      cursor: 'grab',
                      backgroundColor: '#e3f2fd',
                      border: '1px dashed #1976d2',
                      '&:hover': { backgroundColor: '#bbdefb' },
                    }}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('application/json', JSON.stringify({
                        type: 'entry',
                        data: entry,
                      }));
                    }}
                  >
                    <Typography variant="caption" color="primary">
                      📄 Drag entire entry to canvas
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );

  const renderPagesPanel = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Pages ({currentDocument?.pages.length || 0})
      </Typography>
      
      {currentDocument?.pages.map((page, index) => (
        <Paper
          key={page.id}
          sx={{
            p: 1,
            mb: 1,
            cursor: 'pointer',
            border: index === currentPageIndex ? '2px solid #1976d2' : '1px solid #e0e0e0',
            '&:hover': { backgroundColor: '#f5f5f5' },
          }}
          onClick={() => handlePageThumbnailClick(index)}
        >
          <Box
            sx={{
              width: '100%',
              height: 80,
              backgroundColor: page.backgroundColor,
              border: '1px solid #ddd',
              borderRadius: 1,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Mini preview of page elements */}
            {page.elements.slice(0, 3).map((element, elIndex) => (
              <Box
                key={element.id}
                sx={{
                  position: 'absolute',
                  left: `${(element.x / page.width) * 100}%`,
                  top: `${(element.y / page.height) * 100}%`,
                  width: `${Math.min((element.width / page.width) * 100, 20)}%`,
                  height: `${Math.min((element.height / page.height) * 100, 20)}%`,
                  backgroundColor: element.type === 'text' ? '#ddd' : '#bbb',
                  borderRadius: 0.5,
                  opacity: 0.7,
                }}
              />
            ))}
          </Box>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Page {index + 1}
          </Typography>
        </Paper>
      ))}
    </Box>
  );

  const renderCompanyPanel = () => (
    <CompanyImagesSidebar 
      onOpenImageManager={() => {
        // Navigate to company images page
        // Open in new tab so user can manage images while keeping PDF editor open
        window.open('/company-images', '_blank');
      }}
    />
  );

  const sidebarContent = (
    <Box
      sx={{
        width: 320,
        p: 2,
        bgcolor: 'transparent',
        borderRight: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }}
    >
      {/* Assets Header */}
      <Typography
        variant="h6"
        sx={{
          fontFamily: 'Inter, -apple-system, sans-serif',
          fontWeight: 600,
          color: '#2F7A4F',
          mb: 0.5,
          textAlign: 'center'
        }}
      >
        Assets
      </Typography>

      {/* Asset Tab Buttons - Side by Side */}
      <Box sx={{ display: 'flex', gap: 1, px: 1, mb: 0.5 }}>
        <Button
          onClick={() => setSidebarTab(0)}
          variant={sidebarTab === 0 ? 'contained' : 'outlined'}
          startIcon={<Description />}
          sx={{
            flex: 1,
            borderRadius: 2,
            py: 1.5,
            px: 2,
            fontFamily: 'Inter, -apple-system, sans-serif',
            fontWeight: 600,
            textTransform: 'none',
            bgcolor: sidebarTab === 0 ? '#2F7A4F' : 'transparent',
            color: sidebarTab === 0 ? '#FFFFFF' : '#2F7A4F',
            borderColor: '#2F7A4F',
            '&:hover': {
              bgcolor: sidebarTab === 0 ? 'rgba(47, 122, 79, 0.9)' : 'rgba(47, 122, 79, 0.1)',
              borderColor: '#2F7A4F'
            }
          }}
        >
          Report
        </Button>

        <Button
          onClick={() => setSidebarTab(1)}
          variant={sidebarTab === 1 ? 'contained' : 'outlined'}
          startIcon={<ImageIcon />}
          sx={{
            flex: 1,
            borderRadius: 2,
            py: 1.5,
            px: 2,
            fontFamily: 'Inter, -apple-system, sans-serif',
            fontWeight: 600,
            textTransform: 'none',
            bgcolor: sidebarTab === 1 ? '#2F7A4F' : 'transparent',
            color: sidebarTab === 1 ? '#FFFFFF' : '#2F7A4F',
            borderColor: '#2F7A4F',
            '&:hover': {
              bgcolor: sidebarTab === 1 ? 'rgba(47, 122, 79, 0.9)' : 'rgba(47, 122, 79, 0.1)',
              borderColor: '#2F7A4F'
            }
          }}
        >
          Company
        </Button>
      </Box>

      {/* Content area for selected tab */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {sidebarTab === 0 && renderAssetPanel()}
        {sidebarTab === 1 && renderCompanyPanel()}
      </Box>
    </Box>
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading PDF Editor...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'transparent' }}>
      {/* Header - Deep Navy */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          zIndex: 1000, 
          bgcolor: '#142C3F',
          borderRadius: 0,
          boxShadow: 'none'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {/* Left Side - Back Button */}
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton
              onClick={() => navigate('/')}
              sx={{
                color: '#FFFFFF',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#FFFFFF',
                fontWeight: 600,
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                fontSize: '1.5rem'
              }}
            >
              Editor
            </Typography>
            <Box
              sx={{
                bgcolor: '#2F7A4F',
                color: '#FFFFFF',
                borderRadius: '50px',
                px: 2,
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                fontFamily: 'Inter, -apple-system, sans-serif',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Beta
            </Box>
          </Box>
          
          {/* Right Side - Save Status & File Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Save Status Indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {saveStatus === 'saving' && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'rgba(255, 193, 7, 0.1)',
                  px: 2,
                  py: 0.75,
                  borderRadius: '20px',
                  border: '1px solid rgba(255, 193, 7, 0.3)'
                }}>
                  <CircularProgress size={14} sx={{ color: '#FFC107' }} />
                  <Typography sx={{ color: '#FFC107', fontSize: '0.875rem', fontWeight: 500 }}>
                    Saving...
                  </Typography>
                </Box>
              )}
              {saveStatus === 'saved' && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'rgba(76, 175, 80, 0.1)',
                  px: 2,
                  py: 0.75,
                  borderRadius: '20px',
                  border: '1px solid rgba(76, 175, 80, 0.3)'
                }}>
                  <CheckCircle sx={{ color: '#4CAF50', fontSize: 14 }} />
                  <Typography sx={{ color: '#4CAF50', fontSize: '0.875rem', fontWeight: 500 }}>
                    Saved
                  </Typography>
                </Box>
              )}
              {saveStatus === 'unsaved' && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'rgba(255, 87, 34, 0.1)',
                  px: 2,
                  py: 0.75,
                  borderRadius: '20px',
                  border: '1px solid rgba(255, 87, 34, 0.3)'
                }}>
                  <Box sx={{ 
                    width: 6, 
                    height: 6, 
                    borderRadius: '50%', 
                    bgcolor: '#FF5722' 
                  }} />
                  <Typography sx={{ color: '#FF5722', fontSize: '0.875rem', fontWeight: 500 }}>
                    Unsaved changes
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Button
              onClick={handleFileMenuClick}
              variant="outlined"
              sx={{ 
                color: '#FFFFFF',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                padding: '8px 24px',
                borderRadius: '50px',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: '#FFFFFF'
                }
              }}
            >
              Help me create my doc
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => dispatch(setError(null))} sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Drawer
          variant={isMobile ? 'temporary' : 'persistent'}
          anchor="left"
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          sx={{
            // Hide sidebar on small screens to prioritize document view
            display: { xs: sidebarOpen ? 'block' : 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              position: 'relative',
              height: '100%',
              border: 'none',
              borderRight: '1px solid rgba(27, 46, 60, 0.2)',
              bgcolor: '#FFFFFF',
              // Make sidebar narrower on smaller screens
              width: { xs: '280px', sm: '320px', md: '350px' },
            },
          }}
        >
          {sidebarContent}
        </Drawer>

        {/* Main Editor Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Canvas Area */}
          <Box
            data-scroll-container
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              overflow: 'auto',
              bgcolor: 'transparent',
              p: 4,
              pt: 6,
              position: 'relative', // Required for absolute positioning of overlays
            }}
            onDragOver={(e) => e.preventDefault()}
          >
            {currentDocument ? (
              <ExportErrorBoundary onError={(error) => console.warn('📝 Export error boundary caught:', error.message)}>
                <DocumentEditor 
                  ref={documentEditorRef}
                  width={canvasWidth} 
                  height={canvasHeight} 
                  reportId={reportId}
                  reportTitle={reportData?.title}
                  onSaveStatusChange={setSaveStatus}
                  onOverlayPositionsChange={handleOverlayPositionsChange}
                />
              </ExportErrorBoundary>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  width: 600,
                  height: 400,
                  bgcolor: '#FFFFFF',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                  border: '1px solid rgba(226, 232, 240, 0.8)'
                }}
              >
                <Typography 
                  variant="h6"
                  sx={{
                    color: '#64748B',
                    fontFamily: 'Inter, -apple-system, sans-serif',
                    fontWeight: 500
                  }}
                >
                  No document loaded
                </Typography>
                <Button 
                  variant="contained"
                  onClick={() => {
                    //console.log('Manual document creation');
                    dispatch(createDocument({ 
                      title: `Report ${reportId || 'New'} - PDF Editor`,
                      reportId: reportId 
                    }));
                  }}
                  sx={{
                    bgcolor: '#2F7A4F',
                    color: '#FFFFFF',
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontFamily: 'Inter, -apple-system, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: 'rgba(47, 122, 79, 0.9)'
                    }
                  }}
                >
                  Create New Document
                </Button>
              </Paper>
            )}
          </Box>
        </Box>
      </Box>

      {/* Export Progress Bar */}
      {(isExporting || exportProgress > 0) && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 110,
            right: 24,
            width: 200,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 2,
            p: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            zIndex: 1000,
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(47, 122, 79, 0.1)'
          }}
        >
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: '#2F7A4F',
              fontFamily: 'Inter, -apple-system, sans-serif',
              fontWeight: 600,
              fontSize: '0.875rem'
            }}
          >
            Exporting PDF... {Math.round(exportProgress)}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={exportProgress}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'rgba(47, 122, 79, 0.1)',
              '& .MuiLinearProgress-bar': {
                bgcolor: '#2F7A4F',
                borderRadius: 4
              }
            }}
          />
        </Box>
      )}

      {/* Floating Export Button */}
      <IconButton
        onClick={handleExport}
        disabled={isLoading || isExporting}
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          width: 64,
          height: 64,
          bgcolor: '#2F7A4F',
          color: '#FFFFFF',
          boxShadow: '0 8px 24px rgba(47, 122, 79, 0.4)',
          zIndex: 1000,
          '&:hover': {
            bgcolor: 'rgba(47, 122, 79, 0.9)',
            boxShadow: '0 12px 32px rgba(47, 122, 79, 0.5)',
            transform: 'translateY(-2px)'
          },
          '&:disabled': {
            bgcolor: 'rgba(47, 122, 79, 0.3)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        {isExporting ? (
          <Sync 
            sx={{ 
              fontSize: 28,
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              },
              animation: 'spin 1s linear infinite'
            }} 
          />
        ) : (
          <Download sx={{ fontSize: 28 }} />
        )}
      </IconButton>

      {/* New File Template Dialog */}
      <NewFileTemplateDialog
        open={newFileDialogOpen}
        onClose={() => setNewFileDialogOpen(false)}
        onCreateBlank={handleCreateBlankDocument}
        onCreateFromTemplate={handleCreateFromTemplate}
        availableEntries={availableAssets?.entries || []}
      />

      {/* Step 3: React Portal Overlays - Rendered to scroll container for proper absolute positioning */}
      {overlayPositions.map(position => {
        // Parse metadata with date handling
        const metadata = {
          ...position.metadata,
          capturedAt: new Date(position.metadata.capturedAt)
        };

        // Find the scroll container to render portals to the correct positioning context
        const scrollContainer = document.querySelector('[data-scroll-container]');
        if (!scrollContainer) return null;

        return ReactDOM.createPortal(
          <MetadataOverlay
            key={position.id}
            id={position.id}
            metadata={metadata}
            rect={position.rect}
            overlayScale={position.metadata.overlayScale || 1.0}
          />,
          scrollContainer // Render to scroll container for position: absolute context
        );
      }).filter(Boolean)}
    </Box>
  );
};

export default PDFEditor;